import React from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as TrendFireIcon } from '@repo/common/assets/icons/24px/icon_24px_trend-fire_outline.svg';
import { ReactComponent as NavigationIcon } from '@repo/common/assets/icons/20px/icon_20px_navigation-right.svg';

export const PRIORITY_ACTION_PRESETS = [
  {
    id: 'visa-eligibility',
    scoreLabel: 'improve 5 points',
    title: 'Check your visa eligibility',
  },
  {
    id: 'interview-preparation',
    scoreLabel: 'improve 5 points',
    title: 'Complete interview preparation',
  },
  {
    id: 'visa-eligibility-repeat',
    scoreLabel: 'improve 5 points',
    title: 'Check your visa eligibility',
  },
];

const FIXED_PRIORITY_ACTION_COUNT = 3;

const normalizeFixedPriorityActions = (items) => {
  const sourceItems = Array.isArray(items) && items.length > 0
    ? items
    : PRIORITY_ACTION_PRESETS;

  return Array.from({ length: FIXED_PRIORITY_ACTION_COUNT }, (_, index) => ({
    ...PRIORITY_ACTION_PRESETS[index],
    ...sourceItems[index],
  }));
};

function PriorityActionCard({
  scoreLabel = 'improve 5 points',
  pointText,
  title = 'Priority action',
  actionText,
  onClick,
  to,
  path,
  href,
  url,
  className = '',
}) {
  const destination = to || path || href || url;
  const isClickable = typeof onClick === 'function' || Boolean(destination);
  const displayScoreLabel = pointText || scoreLabel;
  const displayTitle = title || actionText;
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (/^https?:\/\//.test(destination)) {
      window.location.assign(destination);
      return;
    }

    if (destination) navigate(destination);
  };

  return (
    <button
      type='button'
      onClick={handleClick}
      disabled={!isClickable}
      className={twMerge(
        'group flex flex-col items-start gap-2 border-[1.6px] border-grayscale-gray02 bg-white/50 text-left transition-colors tabletV:gap-4 tabletV:backdrop-blur-[25px]',
        // Mobile
        'w-[clamp(220px,30vw,346px)] min-w-[220px] max-w-[346px] shrink-0 rounded-[24px] px-[30px] py-6',
        // Tablet and desktop
        'tabletV:h-[120px] tabletV:w-auto tabletV:min-w-0 tabletV:max-w-[420px] tabletV:flex-1 tabletV:basis-[280px] tabletV:shrink tabletV:rounded-[30px] desktopL:h-[160px] desktopL:basis-[346.667px] desktopL:max-w-[346.667px]',
        isClickable
          ? 'cursor-pointer hover:bg-white/70 active:bg-white/80'
          : 'disabled:cursor-default',
        className
      )}
    >
      {/* 상단 행: 모바일 = score + arrow / 데스크탑 = arrow만 */}
      <div className='flex w-full shrink-0 items-end justify-between text-grayscale-gray03 tabletV:items-start tabletV:justify-end'>
        <p className='typo-b13Strong text-text-success tabletV:hidden'>
          {displayScoreLabel}
        </p>
        <NavigationIcon className='size-6 -rotate-45 transition-colors group-hover:text-grayscale-gray05' />
      </div>

      {/* 모바일 제목 */}
      <p className='h-[48px] w-full overflow-hidden text-ellipsis typo-b16Strong text-grayscale-gray09 tabletV:hidden'>
        {displayTitle}
      </p>

      {/* 데스크탑: score + 제목 하단 */}
      <div className='hidden tabletV:flex min-h-0 flex-1 flex-col justify-end gap-0.5 self-stretch'>
        <p className='typo-b14Strong w-full text-text-success'>
          {displayScoreLabel}
        </p>
        <p className='typo-b18Strong w-full text-grayscale-gray09'>
          {displayTitle}
        </p>
      </div>
    </button>
  );
}

function PriorityActions({
  items = PRIORITY_ACTION_PRESETS,
  onActionClick,
  className = '',
}) {
  const fixedItems = normalizeFixedPriorityActions(items);

  return (
    <section className={twMerge('flex w-full flex-col items-start gap-5', className)}>
      <div className='flex w-full items-start gap-1.5 px-5'>
        <span className='flex h-[30px] shrink-0 items-center text-icon-primary'>
          <TrendFireIcon className='size-6' />
        </span>
        <h2 className='typo-b20Strong min-w-0 flex-1 text-text-primary'>
          Priority actions
        </h2>
      </div>

      {/* 모바일: 가로 슬라이드 / 데스크탑: flex-wrap */}
      <div className='flex w-[calc(100%+16px)] gap-[clamp(8px,1.2vw,15px)] overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden tabletV:w-full tabletV:flex-nowrap tabletV:gap-[15px] tabletV:overflow-visible tabletV:pb-0'>
        {fixedItems.map((item, index) => (
          <PriorityActionCard
            key={item.id || `${item.title}-${index}`}
            scoreLabel={item.scoreLabel}
            pointText={item.pointText}
            title={item.title}
            actionText={item.actionText}
            to={item.to}
            path={item.path}
            href={item.href}
            url={item.url}
            onClick={
              onActionClick ? () => onActionClick(item, index) : item.onClick
            }
          />
        ))}
      </div>
    </section>
  );
}

export { PriorityActionCard };
export default PriorityActions;
